import {CSSObject} from '@mantine/core';

import {callOnEnterOrSpace} from 'util/Keyboard';
import {ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

interface DisabledButtonProps {
  'aria-disabled'?: true;
  'data-disabled'?: true;
  tabIndex?: -1;
  onClick: (e: ElementMouseEvent) => void;
  sx?: CSSObject;
}

/**
 * A helper to provide accessible props for a conditionally disabled Mantine `Button` that is wrapped in an
 * explanatory tooltip. This tells screenreader users that the button is disabled, but allows the button to be
 * moused over to trigger the tooltip.
 * The return value of this function should be spread as props on the Mantine `Button`.
 * @param disabled Whether the button is currently disabled.
 * @param onClick Function to be called when the button is not disabled
 * @example
 * ```
 * <Tooltip label="Cannot press button because reasons">
 *   <Button {...getDisabledTooltipButtonProps()}>Do a thing</Button>
 * </Tooltip>
 * ```
 */
export function getDisabledTooltipButtonProps(
  disabled: boolean,
  onClick: (e: ElementMouseEvent) => void
): DisabledButtonProps {
  if (disabled) {
    return {
      'aria-disabled': true,
      'data-disabled': true,
      tabIndex: -1,
      onClick: (e: ElementMouseEvent) => {
        e.preventDefault();
      },
      sx: {'&[data-disabled]': {pointerEvents: 'all'}},
    };
  }
  return {onClick};
}

interface GetActionButtonRolePropsArgs {
  onPress: (e: ElementMouseEvent | ElementKeyboardEvent) => void;
  preventDefault?: boolean;
  stopPropagation?: boolean;
}

export interface ActionButtonRoleProps {
  // FontAwesome sets this to true by default, so we need to explicitly set it to false
  'aria-hidden': false;
  role: 'button';
  tabIndex: number;
  onClick: (e: ElementMouseEvent) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
}

/**
 * A helper to provide accessible props for an 'action button'; that is, a stateless button that performs an
 * action when pressed. Note that this helper is not necessary for `<button>` elements, including UI-Library
 * `Button` and Mantine `Button`. The return value of this function should be spread as props on your element.
 * @param onPress The function to be called when the button is pressed, either by mouse click or with the
 *                Enter key or spacebar.
 * @param [preventDefault=false] Whether to call `e.preventDefault` on the keydown or mouse event.
 * @param [stopPropagation=false] Whether to call `e.stopPropagation` on the keydown or mouse event.
 * @example
 * ```
 * <BenchlingIcon
 *   altText="Close"
 *   icon="close"
 *   {...getActionButtonRoleProps({
 *     onPress: () => {
 *       props.onClose();
 *     }
 *   })}
 * />
 * ```
 */
export function getActionButtonRoleProps({
  onPress,
  preventDefault = false,
  stopPropagation = false,
}: GetActionButtonRolePropsArgs): ActionButtonRoleProps {
  const handlePress = (e: ElementKeyboardEvent | ElementMouseEvent): void => {
    if (preventDefault) {
      e.preventDefault();
    }
    if (stopPropagation) {
      e.stopPropagation();
    }
    onPress(e);
  };

  return {
    'aria-hidden': false,
    role: 'button',
    tabIndex: 0,
    onClick: handlePress,
    onKeyDown: callOnEnterOrSpace((e) => {
      handlePress(e);
    }),
  };
}

export type GetToggleButtonRolePropsArgs = GetActionButtonRolePropsArgs & {
  pressed: boolean;
  partiallyPressed?: boolean;
};

export type ToggleButtonRoleProps = ActionButtonRoleProps & {
  'aria-pressed': boolean | 'mixed';
};

/**
 * A helper to provide accessible props for a 'toggle button'; that is, a button that can be either 'pressed'
 * or 'not pressed'. Note that if you are using a `<button>` element, including UI-Library `Button`, it is
 * probably easier to simply pass your 'pressed' state attribute into `aria-pressed`. The return value of this
 * function should be spread as props on your element.
 * @param onPress The function to be called when the button is pressed, either by mouse click or with the
 *                Enter key or spacebar.
 * @param partiallyPressed Whether the button should be considered 'partially pressed', e.g. with a button
 *                         that can control the behavior of other buttons. This takes arg precedence over the
 *                         'pressed' arg.
 * @param pressed Whether the button should be considered 'pressed'.
 * @param [preventDefault=false] Whether to call `e.preventDefault` on the keydown or mouse event.
 * @param [stopPropagation=false] Whether to call `e.stopPropagation` on the keydown or mouse event.
 * @example
 * ```
 * <BenchlingIcon
 *   altText="DNA"
 *   icon="sequence"
 *   {...getToggleButtonRoleProps({
 *     onPress: () => {
 *       props.setSequenceType();
 *     }
 *   })}
 * />
 * ```
 */
export function getToggleButtonRoleProps({
  onPress,
  partiallyPressed,
  pressed,
  preventDefault = false,
  stopPropagation = false,
}: GetToggleButtonRolePropsArgs): ToggleButtonRoleProps {
  return {
    ...getActionButtonRoleProps({onPress, preventDefault, stopPropagation}),
    'aria-pressed': partiallyPressed ? 'mixed' : pressed,
  };
}
